import React, { useEffect, useState } from 'react';
import { UserCog, ShieldOff, ShieldCheck, Search, X, Plus, AlertTriangle } from 'lucide-react';
import {
  saListSuperAdmins, saSearchUsers, saPromoteSuperAdmin, saDemoteSuperAdmin,
} from '../../lib/db';
import { formatDate } from '../../lib/formatters';

function fullName(u) {
  return [u?.first_name, u?.last_name].filter(Boolean).join(' ').trim();
}

function useDebouncedValue(value, delay = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function PromoteModal({ onClose, onPromoted }) {
  const [query, setQuery]     = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError]     = useState('');
  const [busyId, setBusyId]   = useState(null);
  const debounced = useDebouncedValue(query, 250);

  useEffect(() => {
    if (debounced.trim().length < 2) { setResults([]); return; }
    let cancelled = false;
    setSearching(true);
    setError('');
    saSearchUsers(debounced).then(({ data, error: err }) => {
      if (cancelled) return;
      if (err) setError(err.message || 'Search failed');
      else setResults(data || []);
      setSearching(false);
    });
    return () => { cancelled = true; };
  }, [debounced]);

  const promote = async (user) => {
    if (!window.confirm(`Promote ${fullName(user) || user.email} to Super Admin? They will gain full system access.`)) return;
    setBusyId(user.id);
    const { error: err } = await saPromoteSuperAdmin(user.id);
    setBusyId(null);
    if (err) { setError(err.message || 'Could not promote'); return; }
    onPromoted();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between">
          <h2 className="text-base font-semibold text-white">Promote user to Super Admin</h2>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-800 text-slate-400">
            <X size={16} />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name or email…"
              className="w-full pl-9 pr-3 py-2 rounded-xl bg-slate-800 border border-slate-700 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          {error && (
            <div className="px-3 py-2 rounded-lg bg-red-900/30 border border-red-800 text-red-300 text-sm">
              {error}
            </div>
          )}
          <div className="max-h-72 overflow-y-auto">
            {searching && <p className="text-sm text-slate-500 px-1 py-2">Searching…</p>}
            {!searching && query.trim().length >= 2 && results.length === 0 && (
              <p className="text-sm text-slate-500 px-1 py-2">No users matched.</p>
            )}
            {!searching && query.trim().length < 2 && (
              <p className="text-sm text-slate-500 px-1 py-2">Type at least 2 characters to search.</p>
            )}
            <ul className="divide-y divide-slate-800">
              {results.map((u) => (
                <li key={u.id} className="px-2 py-2 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-white truncate">{fullName(u) || u.email}</p>
                    <p className="text-xs text-slate-500 truncate">
                      {u.email}{u.firm_name ? ` · ${u.firm_name}` : ''}
                    </p>
                  </div>
                  <button
                    onClick={() => promote(u)}
                    disabled={busyId === u.id}
                    className="px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium disabled:opacity-50"
                  >
                    {busyId === u.id ? 'Promoting…' : 'Promote'}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <p className="text-xs text-slate-500 pt-1">
            A super-admin can view, suspend, delete any firm and impersonate any user. Only promote people you fully trust.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function SuperAdminAdminsPage() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [showPromote, setShowPromote] = useState(false);
  const [demoting,    setDemoting]    = useState(null);

  const refresh = () => {
    setLoading(true);
    saListSuperAdmins().then(({ data, error: err }) => {
      if (err) setError(err.message || 'Could not load admins');
      else setAdmins(data || []);
      setLoading(false);
    });
  };
  useEffect(refresh, []);

  const demote = async (admin) => {
    if (admin.is_self) {
      alert('You cannot demote yourself. Ask another super-admin to do it.');
      return;
    }
    if (!window.confirm(`Remove super-admin access from ${fullName(admin) || admin.email}? They will lose access to the System Control Panel.`)) return;
    setDemoting(admin.id);
    const { error: err } = await saDemoteSuperAdmin(admin.id);
    setDemoting(null);
    if (err) { alert(err.message || 'Could not demote'); return; }
    refresh();
  };

  if (loading) return <div className="text-center py-20 text-slate-500">Loading…</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-white">Super Admins</h1>
          <p className="text-slate-400 text-sm mt-0.5">
            {admins.length} user{admins.length === 1 ? '' : 's'} with full system access
          </p>
        </div>
        <button
          onClick={() => setShowPromote(true)}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium transition"
        >
          <Plus size={14} /> Promote user
        </button>
      </div>

      {error && (
        <div className="px-4 py-3 rounded-xl bg-red-900/30 border border-red-800 text-red-300 text-sm">
          {error}
        </div>
      )}

      {admins.length === 1 && (
        <div className="px-4 py-3 rounded-xl bg-amber-900/20 border border-amber-800/50 text-amber-300 text-sm flex items-start gap-2">
          <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
          <p>You are the only super-admin. Promote at least one more person so you can recover access if you ever lose your account.</p>
        </div>
      )}

      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wide text-slate-500 bg-slate-800/40">
            <tr>
              <th className="text-left px-5 py-3 font-medium">User</th>
              <th className="text-left px-5 py-3 font-medium">Home firm</th>
              <th className="text-left px-5 py-3 font-medium">Last login</th>
              <th className="text-left px-5 py-3 font-medium">Member since</th>
              <th className="text-right px-5 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {admins.map((a) => (
              <tr key={a.id} className="hover:bg-slate-800/50 transition">
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2">
                    <UserCog size={14} className="text-indigo-400" />
                    <div>
                      <p className="text-white font-medium">
                        {fullName(a) || a.email}
                        {a.is_self && <span className="ml-2 text-xs text-slate-500">(you)</span>}
                      </p>
                      <p className="text-xs text-slate-500">{a.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-3 text-slate-400 text-sm">{a.firm_name || '—'}</td>
                <td className="px-5 py-3 text-slate-400 text-xs">{a.last_login ? formatDate(a.last_login, { includeTime: true }) : 'Never'}</td>
                <td className="px-5 py-3 text-slate-500 text-xs">{formatDate(a.created_at)}</td>
                <td className="px-5 py-3 text-right">
                  <button
                    onClick={() => demote(a)}
                    disabled={a.is_self || admins.length <= 1 || demoting === a.id}
                    title={
                      a.is_self ? 'You cannot demote yourself' :
                      admins.length <= 1 ? 'Cannot demote the last super-admin' :
                      'Remove super-admin access'
                    }
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition bg-slate-800 hover:bg-red-900/40 hover:text-red-300 text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-slate-800 disabled:hover:text-slate-300"
                  >
                    {a.is_self || admins.length <= 1 ? <ShieldCheck size={12} /> : <ShieldOff size={12} />}
                    {demoting === a.id ? 'Removing…' : 'Remove access'}
                  </button>
                </td>
              </tr>
            ))}
            {admins.length === 0 && (
              <tr>
                <td colSpan={5} className="px-5 py-10 text-center text-slate-500">No super-admins.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showPromote && (
        <PromoteModal
          onClose={() => setShowPromote(false)}
          onPromoted={() => { setShowPromote(false); refresh(); }}
        />
      )}
    </div>
  );
}
